import { useEffect, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db, type BudgetAllocation, type Cycle } from '../db/database'
import {
  closeCycle, confirmAllocation, pendingClose, type CloseAnswers, type CycleClose,
} from '../db/budget'
import { cycleLabelDefined } from '../lib/cycles'
import { formatAmount, formatAriary, parseAmountBounded } from '../lib/money'
import { formatDay, weekdayName } from '../lib/dates'
import { navigate } from '../lib/router'
import { Icon } from './Icon'
import './CycleCloseSheet.css'

/**
 * La clôture d'un cycle, en étapes.
 *
 * C'est le seul moment de l'app où l'on demande beaucoup à l'utilisateur, et
 * c'est justifié : un cycle mal clos fausse tous les suivants. L'ordre des
 * questions n'est pas décoratif — on constate d'abord ce qui a été dépensé, et
 * SEULEMENT ensuite on décide du reste. L'inverse ferait décider du sort d'un
 * argent qui n'existe déjà plus.
 *
 * Les étapes sans objet sont sautées : personne ne doit voir un écran vide.
 */
type Step = 'recap' | 'confirm' | 'atLarge' | 'leftover' | 'restart' | 'budgets'

export function CycleCloseSheet(
  { walletId, close: initial, onDone, onDismiss }: {
    walletId: string
    close: CycleClose
    onDone: () => void
    /**
     * Refermer sans avoir répondu. L'app reste consultable, mais verrouillée en
     * écriture jusqu'à ce que le point soit fait — voir `lib/lock.ts`. Sans
     * cette porte de sortie, un utilisateur qui a besoin de relire ses
     * opérations avant de répondre serait coincé, et répondrait au hasard.
     */
    onDismiss?: () => void
  },
) {
  /*
   * L'état d'ouverture, gelé au montage.
   *
   * `initial` est une PROP VIVANTE : l'accueil la relit à chaque écriture, donc
   * confirmer une journée la remplace par un objet neuf. Le parcours se
   * recalculait alors en cours de route et sautait une étape sans l'avoir
   * montrée. On en garde une photo, et on ne la rafraîchit qu'aux moments
   * choisis.
   */
  const [frozen] = useState(initial)
  const [close, setClose] = useState(initial)
  const [index, setIndex] = useState(0)
  const [confirmed, setConfirmed] = useState<string[]>([])
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [spentDrafts, setSpentDrafts] = useState<Record<string, string>>({})
  const [leftover, setLeftover] = useState<CloseAnswers['leftover']>('available')
  const [income, setIncome] = useState(
    close.expectedIncome === null ? '' : formatAmount(close.expectedIncome))
  // Le rythme en cours, et non « mensuel » en dur : reproposer chaque fois un
  // choix que l'utilisateur n'a pas fait finit par être validé sans le lire.
  const [cycle, setCycle] = useState<Cycle>(initial.cycle)
  const [budgets, setBudgets] = useState<'keep' | 'reset'>('keep')
  const [saving, setSaving] = useState(false)

  // Échap referme : c'est le geste attendu d'une feuille, et l'utilisateur qui
  // ne trouve pas comment sortir finit par répondre n'importe quoi.
  useEffect(() => {
    if (!onDismiss) return
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onDismiss() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onDismiss])

  const categories = useLiveQuery(
    () => db.categories.where({ wallet_id: walletId }).toArray(), [walletId], [])
  const nameOf = (id: string) =>
    categories.find((category) => category.id === id)?.name ?? 'Enveloppe'

  const attente = frozen.unconfirmed.filter((a) => !confirmed.includes(a.id))

  const corrige = Object.values(spentDrafts)
    .reduce((sum, raw) => sum + (parseAmountBounded(raw) ?? 0), 0)
  const reste = Math.max(0, close.leftoverTotal - corrige)

  /*
   * La liste des étapes est figée à l'ouverture, sur l'état INITIAL.
   *
   * Elle se recalculait sur l'état rafraîchi : une fois les journées
   * confirmées, l'étape de confirmation disparaissait de la liste, tout se
   * décalait d'un cran, et l'utilisateur SAUTAIT l'étape suivante sans l'avoir
   * vue. Le rafraîchissement doit corriger les montants, jamais le parcours.
   */
  const steps = useMemo(() => {
    const list: Step[] = ['recap']
    if (frozen.unconfirmed.length > 0) list.push('confirm')
    if (frozen.atLarge.length > 0) list.push('atLarge')
    if (frozen.leftoverTotal > 0) list.push('leftover')
    // Le nouveau cycle se pose toujours : c'est le moment où l'on redit combien
    // on a à gérer, comme au premier lancement.
    list.push('restart')
    list.push('budgets')
    return list
  }, [frozen])

  /*
   * Une étape peut perdre son objet en cours de route sans que le parcours
   * change : corriger les enveloppes « au fil de l'eau » à l'étape précédente
   * peut absorber tout le reste, et demander « il te reste 0 Ar, qu'est-ce
   * qu'on en fait ? » n'a alors aucun sens.
   *
   * On la SAUTE à la navigation plutôt que de la retirer de `steps` : retirer
   * une étape décale l'index de toutes les suivantes, et c'est exactement ce
   * qui faisait sauter une étape non vue (voir le commentaire ci-dessus).
   */
  const utileAvec = (candidat: Step, restant: number) =>
    candidat !== 'leftover' || restant > 0
  const utile = (candidat: Step) => utileAvec(candidat, reste)

  /**
   * L'étape utile suivante dans la direction donnée, sans jamais sortir.
   *
   * `restant` est un PARAMÈTRE et non la valeur du rendu : après les
   * confirmations, `setClose` ne prend effet qu'au rendu suivant, si bien que
   * juger le saut sur `reste` revenait à le juger sur le total d'AVANT. On
   * atterrissait alors sur « il te reste 0 Ar, qu'est-ce qu'on en fait ? » —
   * une question sans objet, et un compteur d'étapes incohérent avec elle,
   * puisque `visibles`, lui, l'excluait déjà.
   */
  function saut(depuis: number, sens: 1 | -1, restant = reste): number {
    let i = depuis + sens
    while (i > 0 && i < steps.length - 1 && !utileAvec(steps[i], restant)) i += sens
    return i
  }

  const step = steps[Math.min(index, steps.length - 1)]
  const dernier = index >= steps.length - 1
  // Le compteur ne promet que des écrans qui seront montrés : « étape 3 sur 5 »
  // alors qu'il en reste deux se lit comme un parcours qui a sauté.
  const visibles = steps.filter(utile)
  const rang = Math.max(0, visibles.indexOf(step))
  // On ne quitte pas cette étape en laissant des journées en suspens : une
  // journée jamais tranchée resterait indéfiniment en retard sur un cycle clos.
  const bloque = step === 'confirm' && attente.length > 0

  async function suivant() {
    // Après les confirmations, les restes ont changé : on relit plutôt que de
    // faire décider l'utilisateur sur des chiffres périmés.
    let courant = close
    if (step === 'confirm') {
      const frais = await pendingClose(walletId)
      if (frais) { setClose(frais); courant = frais }
    }
    // Le saut se juge sur ce qu'on VIENT de lire, jamais sur le rendu en cours.
    setIndex((i) => saut(i, 1, Math.max(0, courant.leftoverTotal - corrige)))
  }

  async function confirmer(allocation: BudgetAllocation) {
    const raw = drafts[allocation.id]
    const amount = raw === undefined ? allocation.planned_amount : parseAmountBounded(raw)
    if (amount === null) return
    await confirmAllocation(allocation, amount)
    setConfirmed((c) => [...c, allocation.id])
  }

  /** Solder toutes les journées d'un coup, aux montants affichés. */
  async function confirmerTout() {
    for (const allocation of attente) await confirmer(allocation)
  }

  async function terminer() {
    setSaving(true)
    const spentByCategory: Record<string, number> = {}
    for (const [id, raw] of Object.entries(spentDrafts)) {
      const amount = parseAmountBounded(raw) ?? 0
      if (amount > 0) spentByCategory[id] = amount
    }
    await closeCycle(walletId, close, {
      spentByCategory,
      leftover,
      income: parseAmountBounded(income),
      cycle,
      budgets,
    })
    onDone()
    // Répartir est l'étape suivante, et elle n'a de sens qu'une fois l'argent
    // posé : on y emmène plutôt que de laisser devant un accueil muet.
    navigate('/budget')
  }

  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true"
         aria-label={`Le point sur ${cycleLabelDefined(close.range, close.cycle)}`}>
      <div className="sheet close-sheet">
        <header className="close-head">
          <h2>Le point sur {cycleLabelDefined(close.range, close.cycle)}</h2>
          <p className="close-steps">Étape {rang + 1} sur {visibles.length}</p>
          {onDismiss && (
            <button type="button" className="close-later" onClick={onDismiss}>
              Plus tard
            </button>
          )}
        </header>

        <div className="close-body">
          {step === 'recap' && (
            <>
              <dl className="recap">
                <div><dt>Reçu</dt><dd className="tabular is-income">{formatAriary(close.received)}</dd></div>
                <div><dt>Dépensé</dt><dd className="tabular is-expense">{formatAriary(close.spent)}</dd></div>
                <div><dt>Non dépensé</dt><dd className="tabular">{formatAriary(close.leftoverTotal)}</dd></div>
              </dl>
              <p className="close-help">
                {close.cycle === 'monthly'
                  ? 'Encore quelques questions pour que le mois qui commence parte sur des chiffres justes.'
                  : 'Encore quelques questions pour que la semaine qui commence parte sur des chiffres justes.'}
              </p>
            </>
          )}

          {step === 'confirm' && (
            <>
              <p className="close-help">
                Ces journées étaient prévues mais jamais confirmées. Dernière
                occasion de dire ce qui a vraiment été dépensé — ce qui reste ici
                sera considéré comme non dépensé.
              </p>
              <ul className="close-list">
                {attente.map((allocation) => (
                  <li key={allocation.id}>
                    <span className="close-what">
                      <strong>{nameOf(allocation.category_id)}</strong>
                      <span>{weekdayName(allocation.planned_date)} {formatDay(allocation.planned_date)}</span>
                    </span>
                    <span className="close-field">
                      <input
                        type="text" inputMode="numeric" className="tabular"
                        aria-label={`Montant dépensé pour ${nameOf(allocation.category_id)}`}
                        value={drafts[allocation.id] ?? formatAmount(allocation.planned_amount)}
                        onChange={(e) =>
                          setDrafts((c) => ({ ...c, [allocation.id]: e.target.value }))}
                      />
                      <span aria-hidden="true">Ar</span>
                    </span>
                    <button type="button" onClick={() => void confirmer(allocation)}>
                      Confirmer
                    </button>
                  </li>
                ))}
              </ul>
              {attente.length > 0 ? (
                <button type="button" className="close-all" onClick={() => void confirmerTout()}>
                  Tout confirmer aux montants affichés
                </button>
              ) : (
                <p className="close-done"><Icon name="check" size={16} /> Tout est confirmé.</p>
              )}
            </>
          )}

          {step === 'atLarge' && (
            <>
              <p className="close-help">
                Rien n'était prévu dans ces enveloppes, donc rien ne dit si
                l'argent a été dépensé. Laisse à zéro ce que tu as gardé.
              </p>
              <ul className="close-list">
                {frozen.atLarge.map((envelope) => (
                  <li key={envelope.category_id}>
                    <span className="close-what">
                      <strong>{nameOf(envelope.category_id)}</strong>
                      <span>{formatAriary(envelope.remaining)} non dépensés</span>
                    </span>
                    <span className="close-field">
                      <input
                        type="text" inputMode="numeric" className="tabular"
                        aria-label={`Dépensé sans le noter dans ${nameOf(envelope.category_id)}`}
                        value={spentDrafts[envelope.category_id] ?? '0'}
                        onChange={(e) => setSpentDrafts(
                          (c) => ({ ...c, [envelope.category_id]: e.target.value }))}
                      />
                      <span aria-hidden="true">Ar</span>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {step === 'leftover' && (
            <>
              <p className="close-intro">
                Il te reste <strong className="tabular">{formatAriary(reste)}</strong>.
                Qu'est-ce qu'on en fait ?
              </p>
              <div className="close-choices">
                {close.savingsCategoryId && (
                  <label>
                    <input type="radio" name="leftover" checked={leftover === 'savings'}
                           onChange={() => setLeftover('savings')} />
                    <span>
                      <strong>Le mettre de côté</strong>
                      Il rejoint ton épargne et n'en ressort que si tu le décides.
                    </span>
                  </label>
                )}
                <label>
                  <input type="radio" name="leftover" checked={leftover === 'available'}
                         onChange={() => setLeftover('available')} />
                  <span>
                    <strong>Le garder pour {cycleLabelDefined(close.next, close.cycle)}</strong>
                    Il redevient de l'argent à répartir. Rien n'est écrit : il n'a
                    jamais quitté ta poche.
                  </span>
                </label>
                <label>
                  <input type="radio" name="leftover" checked={leftover === 'spent'}
                         onChange={() => setLeftover('spent')} />
                  <span>
                    <strong>En fait je l'ai dépensé</strong>
                    Il sort de ton solde. À choisir si tu n'as pas tout saisi et
                    que cet argent n'est plus là.
                  </span>
                </label>
              </div>
            </>
          )}

          {step === 'restart' && (
            <>
              {/* Ce n'est PAS un premier lancement : on ne souhaite pas la
                  bienvenue à quelqu'un qui utilise l'app depuis des mois. On
                  l'invite à programmer la période qui vient. */}
              <p className="close-intro">
                <strong>Programmons {cycleLabelDefined(close.next, cycle)}.</strong> Comme
                chaque fois : on décide où va l'argent avant de le dépenser.
              </p>

              <fieldset className="close-choices">
                <legend>À quel rythme ton argent rentre-t-il ?</legend>
                <label>
                  <input type="radio" name="cycle" checked={cycle === 'monthly'}
                         onChange={() => setCycle('monthly')} />
                  <span>
                    <strong>Chaque mois</strong>
                    Salaire, pension, loyer reçu…
                  </span>
                </label>
                <label>
                  <input type="radio" name="cycle" checked={cycle === 'weekly'}
                         onChange={() => setCycle('weekly')} />
                  <span>
                    <strong>Chaque semaine</strong>
                    Commerce, journée, petits travaux…
                  </span>
                </label>
              </fieldset>

              <p className="close-legend">
                Combien as-tu à gérer pour {cycleLabelDefined(close.next, cycle)} ?
              </p>
              <label className="close-amount">
                <input type="text" inputMode="numeric" className="tabular"
                       aria-label="Argent disponible pour le nouveau cycle"
                       value={income} placeholder="0"
                       onChange={(e) => setIncome(e.target.value)} />
                <span>Ar</span>
              </label>
              <p className="close-help">
                Compte l'argent que tu as <strong>maintenant</strong>, reste du
                cycle écoulé compris. Si tes revenus ne sont pas fixes, mets ton
                estimation : on te la reproposera à la prochaine clôture.
              </p>
            </>
          )}

          {step === 'budgets' && (
            <>
              <p className="close-intro">Et tes enveloppes ?</p>
              <div className="close-choices">
                <label>
                  <input type="radio" name="budgets" checked={budgets === 'keep'}
                         onChange={() => setBudgets('keep')} />
                  <span>
                    <strong>Reprendre les mêmes montants</strong>
                    Tu retrouves la répartition que tu viens d'utiliser et tu
                    l'ajustes si besoin.
                  </span>
                </label>
                <label>
                  <input type="radio" name="budgets" checked={budgets === 'reset'}
                         onChange={() => setBudgets('reset')} />
                  <span>
                    <strong>Repartir de zéro</strong>
                    Toutes les enveloppes reviennent à 0, tu réparties tout à neuf.
                  </span>
                </label>
              </div>
            </>
          )}
        </div>

        <footer className="close-foot">
          {index > 0 && (
            <button type="button" className="close-back" onClick={() => setIndex((i) => saut(i, -1))}>
              Retour
            </button>
          )}
          <button type="button" className="close-next" disabled={saving || bloque}
                  onClick={() => void (dernier ? terminer() : suivant())}>
            {bloque ? `${attente.length} journée${attente.length > 1 ? 's' : ''} à trancher`
                    : dernier ? 'Terminer' : 'Continuer'}
          </button>
        </footer>
      </div>
    </div>
  )
}
